import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import {
  useUpdate,
  useNotify,
  SimpleForm,
  Edit,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  useDataProvider,
  minLength,
  AutocompleteInput,
  TextInput,
  useTranslate,
  useRecordContext,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import PurchaseReturnItemForm from "./components/PurchaseReturnItemForm";
import RadioGroupX from "../common/RadioGroupX";
import currentDate from "../../../utils/currentDate";
import CodeNumberInput from "../common/CodeNumberInput";

const validateNumber = [required(), minLength(13), maxLength(13)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateSupplier = [required(), minLength(36), maxLength(36)];

const PurchaseItemHydrator = () => {
  const record = useRecordContext();
  const { getValues, setValue } = useFormContext();
  const dataProvider = useDataProvider();

  useEffect(() => {
    let ignore = false;
    const purchaseItems = record?.purchase_return_items ?? [];
    if (purchaseItems.length === 0) {
      return undefined;
    }

    purchaseItems.forEach((item, index) => {
      const source = `purchase_return_items.${index}.purchase_item.purchase_id`;
      const purchaseId =
        item?.purchase_item?.purchase_id ||
        item?.purchase_item?.purchase_order_item?.purchase_order_id ||
        item?.purchase_item?.purchase_order_item?.purchase_order?.id;

      if (purchaseId) {
        if (getValues(source) !== purchaseId) {
          setValue(source, purchaseId, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
        return;
      }

      const purchaseItemId = item?.purchase_item_id || item?.purchase_item?.id;
      if (!purchaseItemId) return;

      dataProvider
        .getOne("purchase-items", { id: purchaseItemId })
        .then(({ data }) => {
          if (ignore || !data?.purchase_id) return;
          if (getValues(source) !== data.purchase_id) {
            setValue(source, data.purchase_id, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
          }
        })
        .catch(() => {});
    });

    return () => {
      ignore = true;
    };
  }, [record, dataProvider, getValues, setValue]);

  return null;
};

export default function PurchaseReturnEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const dataProvider = useDataProvider();
  const t = useTranslate();

  // GET SUPPLIER LISTS
  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error: supplierError,
  } = useQuery(["suppliers/select", "getListNoParams"], () =>
    dataProvider.getListNoParams("suppliers/select")
  );
  // GET SUPPLIER LISTS

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          } else {
            // The shape of the returned validation errors must match the shape of the form
            return error.body.errors;
          }
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth={false}
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <PurchaseItemHydrator />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {t("mine.label.edit")} {t("mine.list_title.purchase_return")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              label="mine.label.date"
              fullWidth
              validate={validateDate}
              defaultValue={currentDate()}
            />
            <AutocompleteInput
              variant="outlined"
              source="supplier_id"
              label="mine.label.supplier"
              choices={isLoadingSuppliers ? [] : suppliers}
              fullWidth
              validate={validateSupplier}
            />

            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              rows={3}
            />

            <RadioGroupX />

            <Box
              sx={{
                  maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                  overflowY: 'auto',
                  border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                  borderRadius: '4px',
                  p: 1, // Opsional: padding
              }}
              >
              <PurchaseReturnItemForm />
            </Box>
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
